import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Send, Paperclip, Trash2, Save, Eye, Type, Loader2, X, Minus, Maximize2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { RichTextEditor } from '../../ui/rich-text-editor';
import { toast } from 'sonner';
import { sanitizeHtml } from '../../../lib/sanitize';
import { applyPlaceholders, htmlToText, textToHtml, isBlankHtml, readFileAsBase64 } from '../../../lib/emailTemplates';
import { RecipientField } from './RecipientField';
import { TemplatePicker, useReplyTemplates } from './TemplatePicker';
import { AttachmentChips } from './AttachmentChips';
import { useCompanyBrand } from '../../../hooks/useCompanyBrand';
import { SchedulePicker, fmtWhen } from './SchedulePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_ATTACH = 8 * 1024 * 1024;

const BrandedPreview = ({ html, agentName, attachments }) => (
  <div className="rounded-lg bg-stone-100 p-4" data-testid="compose-preview">
    <div className="mx-auto max-w-[600px] bg-white border border-stone-200 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-stone-200 font-serif text-lg tracking-wide text-neutral-900">Apollo Shades</div>
      <div className="px-6 py-5 text-[14px] leading-relaxed text-neutral-800 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-amber-700" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
      {attachments.length > 0 && <div className="px-6 pb-4 text-xs text-neutral-600"><strong>Attachments</strong><ul className="list-disc pl-5 mt-1">{attachments.map((a, i) => <li key={i} className="text-amber-700">{a.filename}</li>)}</ul></div>}
      <div className="px-6 py-3 border-t border-stone-200 text-[11px] text-neutral-500">— {agentName} · Apollo Shades</div>
    </div>
  </div>
);

/**
 * Gmail-style composer. `onSend(payload)` must return a promise; payload = {to, cc, bcc, subject, html, text, attachments, template_id}.
 * `initial` can contain to/cc/bcc (arrays), subject, html, in_reply_to, draft_id. `extras` renders custom options next to the footer.
 */
export const EmailComposer = ({ open, onOpenChange, token, initial, onSend, onSchedule, title = 'New message', extras, allowDraft = true, placeholders = {}, agentName = 'Apollo Shades', sendLabel = 'Send' }) => {
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [plain, setPlain] = useState(false);
  const [preview, setPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef();
  const [templates] = useReplyTemplates(token, open);
  const brand = useCompanyBrand();
  const values = useMemo(() => ({ company: brand?.brand_name || '', agent_name: agentName, ...placeholders }), [placeholders, agentName, brand]);

  useEffect(() => {
    if (!open) return;
    const i = initial || {};
    setTo(i.to || []); setCc(i.cc || []); setBcc(i.bcc || []);
    setShowCc(!!i.cc?.length); setShowBcc(!!i.bcc?.length);
    setSubject(i.subject || ''); setHtml(i.html || ''); setText(htmlToText(i.html || ''));
    setPlain(false); setPreview(false); setAttachments([]); setTemplateId(null); setDraftId(i.draft_id || null); setExpanded(false);
  }, [open, initial]);

  const bodyHtml = plain ? textToHtml(text) : html;
  const canSend = to.length > 0 && subject.trim() && !isBlankHtml(bodyHtml) && !busy;

  const pickTemplate = (t) => {
    const body = applyPlaceholders(t.body_html || '', values);
    setTemplateId(t.id);
    if (!subject.trim() || templateId) setSubject(applyPlaceholders(t.subject || '', values));
    if (plain) setText(htmlToText(body)); else setHtml(body);
    toast.success(`Template "${t.name}" inserted`);
  };
  const togglePlain = () => { if (plain) setHtml(textToHtml(text)); else setText(htmlToText(html)); setPlain(!plain); };

  const addFiles = async (files) => {
    const list = [...files];
    const total = attachments.reduce((s, a) => s + a.size, 0) + list.reduce((s, f) => s + f.size, 0);
    if (total > MAX_ATTACH) return toast.error('Attachments are limited to 8 MB per email');
    const read = await Promise.all(list.map(async (f) => ({ filename: f.name, content_type: f.type || 'application/octet-stream', size: f.size, content: await readFileAsBase64(f) })));
    setAttachments((prev) => [...prev, ...read]);
  };

  const payload = () => ({ to, cc, bcc, subject: subject.trim(), html: bodyHtml, text: plain ? text : htmlToText(html), attachments: attachments.map(({ size, ...a }) => a), template_id: templateId, in_reply_to: initial?.in_reply_to || null, draft_id: draftId });
  const send = async () => {
    setBusy('send');
    try {
      await onSend(payload());
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the email');
    } finally { setBusy(null); }
  };
  const schedule = async (sendAt) => {
    setBusy('schedule');
    try {
      await onSchedule(payload(), sendAt);
      toast.success(`Scheduled for ${fmtWhen(sendAt)}`);
      onOpenChange(false, { scheduled: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not schedule the email');
    } finally { setBusy(null); }
  };

  const saveDraft = async () => {
    setBusy('draft');
    try {
      const body = { to, cc, bcc, subject, html: bodyHtml, in_reply_to: initial?.in_reply_to || null };
      const headers = { Authorization: `Bearer ${token}` };
      if (draftId) await axios.put(`${API_URL}/admin/email-drafts/${draftId}`, body, { headers });
      else { const { data } = await axios.post(`${API_URL}/admin/email-drafts`, body, { headers }); setDraftId(data.id); }
      toast.success('Draft saved');
      onOpenChange(false, { draftSaved: true });
    } catch { toast.error('Could not save the draft'); } finally { setBusy(null); }
  };

  const discard = async () => {
    if (draftId) { try { await axios.delete(`${API_URL}/admin/email-drafts/${draftId}`, { headers: { Authorization: `Bearer ${token}` } }); } catch { /* ignore */ } }
    onOpenChange(false, { discarded: true });
  };

  if (!open) return null;
  return (
    <div className={`fixed z-[60] flex flex-col bg-white shadow-2xl border border-neutral-200 overflow-hidden transition-all ${expanded ? 'inset-4 md:inset-10 rounded-2xl' : 'inset-x-0 bottom-0 sm:inset-auto sm:right-6 sm:bottom-0 w-full sm:w-[640px] max-h-[88vh] rounded-t-2xl'}`}
      data-testid="email-composer" role="dialog" aria-label={title}
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
      <div className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white shrink-0">
        <span className="text-xs font-medium truncate flex-1" data-testid="composer-title">{title}{draftId ? ' · draft' : ''}{initial?.send_at ? ` · scheduled ${fmtWhen(initial.send_at)}` : ''}</span>
        <button type="button" onClick={() => setExpanded(!expanded)} className="p-1 rounded hover:bg-white/10" title={expanded ? 'Shrink' : 'Expand'} data-testid="composer-expand">{expanded ? <Minus className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}</button>
        <button type="button" onClick={() => onOpenChange(false)} className="p-1 rounded hover:bg-white/10" title="Close" data-testid="composer-close"><X className="w-3.5 h-3.5" /></button>
      </div>

      <div className="px-4 shrink-0">
        <RecipientField label="To" value={to} onChange={setTo} token={token} autoFocus={!to.length} testId="compose-to"
          right={<div className="flex gap-1 pt-1 text-[11px] text-neutral-500">{!showCc && <button type="button" onClick={() => setShowCc(true)} className="hover:text-neutral-900" data-testid="compose-show-cc">Cc</button>}{!showBcc && <button type="button" onClick={() => setShowBcc(true)} className="hover:text-neutral-900" data-testid="compose-show-bcc">Bcc</button>}</div>} />
        {showCc && <RecipientField label="Cc" value={cc} onChange={setCc} token={token} testId="compose-cc" />}
        {showBcc && <RecipientField label="Bcc" value={bcc} onChange={setBcc} token={token} testId="compose-bcc" />}
        <div className="flex items-center gap-2 border-b border-neutral-200 py-1.5">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="flex-1 h-7 bg-transparent outline-none text-sm font-medium" data-testid="compose-subject" />
          <TemplatePicker templates={templates} onPick={pickTemplate} compact />
          <button type="button" onClick={togglePlain} className={`h-7 px-2 rounded-md text-[11px] inline-flex items-center gap-1 ${plain ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`} title="Toggle plain text" data-testid="compose-plain-toggle"><Type className="w-3.5 h-3.5" />{plain ? 'Plain' : 'Rich'}</button>
          <button type="button" onClick={() => setPreview(!preview)} className={`h-7 px-2 rounded-md text-[11px] inline-flex items-center gap-1 ${preview ? 'bg-amber-100 text-amber-800' : 'text-neutral-600 hover:bg-neutral-100'}`} title="Preview the branded email" data-testid="compose-preview-toggle"><Eye className="w-3.5 h-3.5" />Preview</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px]">
        {preview ? <BrandedPreview html={bodyHtml} agentName={agentName} attachments={attachments} />
          : plain ? <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={expanded ? 18 : 9} placeholder="Write your message…" className="text-sm resize-none border-0 shadow-none focus-visible:ring-0 px-0" data-testid="compose-body-plain" />
          : <div data-testid="compose-body-rich"><RichTextEditor value={html} onChange={setHtml} placeholder="Write your message…" className="border-0 [&_.ProseMirror]:min-h-[180px] [&_.ProseMirror]:text-sm" /></div>}
        <AttachmentChips items={attachments} onRemove={(i) => setAttachments(attachments.filter((_, j) => j !== i))} />
      </div>

      <div className="flex items-center gap-2 px-4 py-2.5 border-t border-neutral-200 bg-neutral-50 shrink-0 flex-wrap">
        <Button size="sm" onClick={send} disabled={!canSend} className="h-8 bg-amber-600 hover:bg-amber-700 gap-1.5 rounded-full px-4" data-testid="compose-send">
          {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}{sendLabel}
        </Button>
        {onSchedule && <SchedulePicker onPick={schedule} disabled={!canSend} busy={busy === 'schedule'} initial={initial?.send_at} label={initial?.send_at ? 'Reschedule' : 'Send later'} />}
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="compose-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} className="p-1.5 rounded-md text-neutral-600 hover:bg-neutral-200" title="Attach files" data-testid="compose-attach-btn"><Paperclip className="w-4 h-4" /></button>
        {allowDraft && <button type="button" onClick={saveDraft} disabled={!!busy || (!subject && isBlankHtml(bodyHtml))} className="p-1.5 rounded-md text-neutral-600 hover:bg-neutral-200 disabled:opacity-40" title="Save draft" data-testid="compose-save-draft">{busy === 'draft' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}</button>}
        <div className="flex-1 min-w-[8px]">{extras}</div>
        <span className="text-[10px] text-neutral-400 hidden sm:inline">Signed as {agentName}</span>
        <button type="button" onClick={discard} className="p-1.5 rounded-md text-neutral-600 hover:bg-red-50 hover:text-red-600" title="Discard" data-testid="compose-discard"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export default EmailComposer;
